import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { live } from '../data/live';
import type { AdminTopUp } from '../data/live';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/**
 * Cash top-ups waiting on a decision.
 *
 * A driver hands cash to an agent and raises a request from the app; the money
 * only exists once somebody confirms it. An agent confirms at their own
 * counter, and this is the operator's view of the same queue — for the cases an
 * agent cannot settle: a disputed amount, a counter that went offline
 * mid-request, or a top-up that wants a second pair of eyes.
 *
 * Approving writes a ledger entry, and the ledger IS the balance. There is no
 * stored total to correct afterwards, which is the point — but it also means an
 * approval is real money and is worth one deliberate click.
 */
const STATES = ['awaiting_agent', 'confirmed', 'rejected', 'expired'] as const;
type State = (typeof STATES)[number];

const LABEL: Record<State, string> = {
  awaiting_agent: 'Waiting',
  confirmed: 'Confirmed',
  rejected: 'Rejected',
  expired: 'Expired',
};

const TONE: Record<State, 'on' | 'off' | 'mid'> = {
  awaiting_agent: 'mid',
  confirmed: 'on',
  rejected: 'off',
  expired: 'off',
};

export function TopUps() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<'waiting' | 'all'>('waiting');
  const [deciding, setDeciding] = useState<AdminTopUp | null>(null);

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['admin-topups'],
    queryFn: () => live.topUps(),
    refetchInterval: 20_000,
  });

  const decide = useMutation({
    mutationFn: (v: { id: string; approve: boolean; amountMinor?: number; reason?: string }) =>
      live.decideTopUp(v.id, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-topups'] });
      setDeciding(null);
    },
    /* Someone else (usually the agent at the counter) may have settled it
       while this dialog was open. Refetch so the row shows what happened. */
    onError: () => { void qc.invalidateQueries({ queryKey: ['admin-topups'] }); },
  });

  /* The mutation outlives the dialog, so its error did too: the next top-up
     opened greeted the operator with the previous one's refusal. Reset on
     every open. */
  const openDecide = (t: AdminTopUp) => { decide.reset(); setDeciding(t); };

  const items = data ?? [];
  const waiting = useMemo(
    () => items.filter((t) => t.state === 'awaiting_agent'),
    [items],
  );
  const shown = filter === 'waiting' ? waiting : items;

  const money = (minor: number, cur: string) =>
    `${cur} ${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

  if (isLoading) return <Loading />;
  // A failed load is said, not shown as an empty, all-clear queue.
  if (!data) return <Empty title="Top-ups are not available." hint={loadFailedHint(loadError)} />;

  return (
    <>
      <div className="tiles">
        <Tile k="Waiting" v={waiting.length}
              d={waiting.length ? 'a driver has paid and is not credited' : 'queue clear'} />
        <Tile k="Confirmed" v={items.filter((t) => t.state === 'confirmed').length}
              d="credited to a wallet" />
        <Tile k="Rejected" v={items.filter((t) => t.state === 'rejected').length}
              d="no money moved" />
        <Tile k="Expired" v={items.filter((t) => t.state === 'expired').length}
              d="nobody confirmed in time" />
      </div>

      <div className="chips" style={{ margin: '14px 0 10px' }}>
        <Chip label={`Waiting (${waiting.length})`} selected={filter === 'waiting'}
              onClick={() => setFilter('waiting')} />
        <Chip label="All" selected={filter === 'all'} onClick={() => setFilter('all')} />
      </div>

      {shown.length === 0 ? (
        <Empty
          title={filter === 'waiting' ? 'Nothing waiting' : 'No top-ups yet'}
          hint={filter === 'waiting'
            ? 'Every request has been settled, by an agent or here.'
            : 'A driver raises one from the app after handing cash to an agent.'}
        />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Requested</th><th>Driver</th><th>Agent</th>
                <th>Amount</th><th>Balance</th><th>State</th><th />
              </tr>
            </thead>
            <tbody>
              {shown.map((t) => (
                <tr key={t.id}>
                  <td className="mono nowrap">
                    {new Date(t.requestedAt).toLocaleString()}
                  </td>
                  <td>
                    {t.driverName}
                    {t.driverEmail && <div className="li-sub">{t.driverEmail}</div>}
                  </td>
                  <td>{t.agentName ?? <span className="faint">—</span>}</td>
                  <td className="mono nowrap">{money(t.amountMinor, t.currency)}</td>
                  <td className="mono nowrap">{money(t.balanceMinor, t.currency)}</td>
                  <td>
                    <Pill tone={TONE[t.state as State] ?? 'off'}>
                      {LABEL[t.state as State] ?? t.state}
                    </Pill>
                    {t.rejectReason && <div className="li-sub">{t.rejectReason}</div>}
                  </td>
                  <td className="act">
                    {t.state === 'awaiting_agent' && (
                      <button className="btn sm" onClick={() => openDecide(t)}>Settle</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="note" style={{ marginTop: 12 }}>
        <b>Approving moves money.</b> It writes a wallet entry, and the balance is
        summed from those entries rather than stored — so there is no total to
        correct afterwards. Confirm the cash was actually handed over.
      </div>

      {deciding && (
        <Decide
          topUp={deciding}
          pending={decide.isPending}
          error={decide.error ? errorText(decide.error) : null}
          onClose={() => setDeciding(null)}
          onDecide={(v) => decide.mutate({ id: deciding.id, ...v })}
        />
      )}
    </>
  );
}

function Decide({ topUp, pending, error, onClose, onDecide }: {
  topUp: AdminTopUp;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onDecide: (v: { approve: boolean; amountMinor?: number; reason?: string }) => void;
}) {
  /* Pre-filled with what was asked for, and editable: the driver handed over
     what they handed over, not what they typed into the app. */
  const [amount, setAmount] = useState((topUp.amountMinor / 100).toString());
  const [reason, setReason] = useState('');

  const n = Number(amount);
  const bad = !(n > 0) ? 'Enter an amount greater than zero.' : null;
  const differs = Math.round(n * 100) !== topUp.amountMinor;

  return (
    <div
      role="dialog" aria-label="Settle top-up"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 420 }}>
        <div className="li-title" style={{ fontSize: 15 }}>
          {topUp.driverName} · {topUp.currency} {(topUp.amountMinor / 100).toFixed(2)}
        </div>
        <div className="li-sub" style={{ marginBottom: 12 }}>
          Requested {new Date(topUp.requestedAt).toLocaleString()}
          {topUp.agentName ? ` at ${topUp.agentName}` : ''}.
        </div>

        <label className="li-sub" htmlFor="tu-amount">Amount to credit</label>
        <input id="tu-amount" className="field mono" value={amount} inputMode="decimal"
               onChange={(e) => setAmount(e.target.value)} />
        <div className="li-sub faint" style={{ marginTop: 4, marginBottom: 10 }}>
          {differs
            ? `Differs from the ${topUp.currency} ${(topUp.amountMinor / 100).toFixed(2)} requested — the audit log records both.`
            : 'As requested.'}
        </div>

        <label className="li-sub" htmlFor="tu-reason">Reason, if rejecting</label>
        <input id="tu-reason" className="field" value={reason}
               placeholder="No cash received" onChange={(e) => setReason(e.target.value)} />

        {(bad || error) && (
          <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 10 }}>
            {error ?? bad}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                  onClick={() => onDecide({ approve: true, amountMinor: Math.round(n * 100) })}>
            {pending ? 'Working…' : 'Approve and credit'}
          </button>
          <button className="btn ghost" style={{ flex: 1 }} disabled={pending}
                  onClick={() => onDecide({ approve: false, reason })}>
            Reject
          </button>
        </div>
        <button className="btn ghost sm" style={{ width: '100%', marginTop: 8 }}
                onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}
